import { useEffect, useRef, useState } from "react";
import {
  Box,
  Typography,
  FormControl,
  FormControlLabel,
  FormLabel,
  RadioGroup,
  CircularProgress,
} from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import {
  getQuestionnaireAnswers,
  getQuestionnaireById,
} from "../../services/questionnaireService";
import { InputText } from "../../elements/InputText";
import RadioControl from "../../elements/RadioControl";

type TRebookQuestionnaireSectionProps = {
  jobId?: string;
  isEditing?: boolean;
  onAnswersChange: (content: any[] | null, hasPrevious: boolean) => void;
};

const formatAnswer = (answers: any): string => {
  if (answers == null || answers === "") {
    return "—";
  }
  if (typeof answers === "string") {
    return answers;
  }
  if (typeof answers === "object" && answers.text) {
    return answers.text;
  }
  return String(answers);
};

const parseContent = (content: any) => {
  if (!content) {
    return null;
  }
  if (typeof content === "string") {
    try {
      return JSON.parse(content);
    } catch {
      return null;
    }
  }
  return content;
};

const buildContent = (fields: any[], values: Record<string, any>) => {
  return Object.keys(values).reduce((acc: any[], item: string) => {
    const questionField = fields.find((que: any) => que.id === item);
    if (!questionField) {
      return acc;
    }
    const answer = questionField?.options
      ? questionField.options.find((opt: any) => opt.id === values[item])
      : values[item];
    if (answer) {
      acc.push({
        question: questionField.question,
        answers: answer,
      });
    }
    return acc;
  }, []);
};

export const RebookQuestionnaireSection = ({
  jobId,
  isEditing = false,
  onAnswersChange,
}: TRebookQuestionnaireSectionProps) => {
  const [fields, setFields] = useState<any[] | null>(null);
  const [answers, setAnswers] = useState<any[] | null>(null);
  const [summaryAnswers, setSummaryAnswers] = useState<any[] | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const { control, setValue, watch } = useForm();
  const onAnswersChangeRef = useRef(onAnswersChange);
  onAnswersChangeRef.current = onAnswersChange;
  const didPrefill = useRef(false);

  const hasPrevious = Boolean(summaryAnswers?.length);

  useEffect(() => {
    if (!jobId) {
      return;
    }

    const load = async () => {
      setIsLoading(true);
      didPrefill.current = false;
      try {
        const [questionRes, answersRes] = await Promise.all([
          getQuestionnaireById({ id: jobId }),
          getQuestionnaireAnswers({ id: jobId }).catch(() => null),
        ]);
        const parsedFields = parseContent(questionRes?.content);
        const parsedAnswers = parseContent(answersRes?.content);
        setFields(Array.isArray(parsedFields) ? parsedFields : null);
        setAnswers(Array.isArray(parsedAnswers) ? parsedAnswers : null);
        setSummaryAnswers(Array.isArray(parsedAnswers) ? parsedAnswers : null);
        onAnswersChangeRef.current(null, Boolean(parsedAnswers?.length));
      } catch (error) {
        setFields(null);
        setAnswers(null);
        setSummaryAnswers(null);
        onAnswersChangeRef.current(null, false);
      } finally {
        setIsLoading(false);
      }
    };

    load();
  }, [jobId]);

  useEffect(() => {
    if (didPrefill.current || !fields || !answers?.length) {
      return;
    }
    answers.forEach((field: any) => {
      const question = fields.find((que: any) => que.question === field.question);
      if (!question) {
        return;
      }
      const value = question?.options ? field?.answers?.id : field?.answers;
      setValue(question.id, value, { shouldTouch: true });
    });
    didPrefill.current = true;
  }, [fields, answers, setValue]);

  useEffect(() => {
    if (!isEditing || !fields) {
      return;
    }
    const subscription = watch((values) => {
      const content = buildContent(fields, values as Record<string, any>);
      if (content.length) {
        setSummaryAnswers(content);
      }
      onAnswersChangeRef.current(content.length ? content : null, hasPrevious || content.length > 0);
    });
    return () => subscription.unsubscribe();
  }, [isEditing, fields, hasPrevious, watch]);

  const renderFields = () => {
    if (!fields) {
      return null;
    }

    return fields.map((field: any, index: number) => {
      if (field?.isLabel) {
        return (
          <FormLabel
            key={field.id + index}
            sx={{ display: "block", mb: "8px" }}
            focused={false}
          >
            <span dangerouslySetInnerHTML={{ __html: field.question }} />
          </FormLabel>
        );
      }
      if (field?.options) {
        return (
          <FormControl key={field.id + index} sx={{ mb: "16px" }} component="fieldset">
            <FormLabel sx={{ mb: "8px" }} focused={false} component="legend">
              <span dangerouslySetInnerHTML={{ __html: field.question }} />
            </FormLabel>
            <Controller
              name={field.id}
              control={control}
              defaultValue=""
              render={({ field: controllerField }) => (
                <RadioGroup {...controllerField}>
                  {field.options.map((option: any) => (
                    <FormControlLabel
                      key={option.id}
                      value={option.id}
                      control={<RadioControl />}
                      label={option.text}
                    />
                  ))}
                </RadioGroup>
              )}
            />
          </FormControl>
        );
      }
      return (
        <FormControl key={field.id + index} sx={{ mb: "16px", width: "100%" }} component="fieldset">
          <FormLabel sx={{ mb: "8px" }} component="legend">
            <span dangerouslySetInnerHTML={{ __html: field.question }} />
          </FormLabel>
          <Controller
            name={field.id}
            control={control}
            render={({ field: controllerField }) => (
              <InputText
                inputProps={{ ...controllerField }}
                placeholder="Answer"
                multiline
                rows={2}
                variant="outlined"
                fullWidth
              />
            )}
          />
        </FormControl>
      );
    });
  };

  return (
    <Box sx={{ mt: "12px" }}>
      <Typography sx={{ fontSize: "12px", lineHeight: "16px", color: "#6B746B", mb: "6px" }}>
        Access notes
      </Typography>

      {isLoading && (
        <Box sx={{ display: "flex", justifyContent: "flex-start", py: "8px" }}>
          <CircularProgress size={18} />
        </Box>
      )}

      {!isLoading && !isEditing && hasPrevious && summaryAnswers &&
        summaryAnswers.map((item: any, index: number) => (
          <Box key={`${item.question}-${index}`} sx={{ mb: "8px" }}>
            <Typography
              sx={{ fontSize: "12px", lineHeight: "16px", color: "#6B746B", mb: "2px" }}
              dangerouslySetInnerHTML={{ __html: item.question }}
            />
            <Typography sx={{ fontSize: "14px", lineHeight: "20px", color: "#0A1E0B" }}>
              {formatAnswer(item.answers)}
            </Typography>
          </Box>
        ))}

      {!isLoading && !isEditing && !hasPrevious && (
        <Typography sx={{ fontSize: "14px", lineHeight: "20px", color: "#0A1E0B" }}>
          No access notes from last time.
        </Typography>
      )}

      <Box
        sx={{ display: isEditing && fields?.length ? "block" : "none" }}
        aria-hidden={!isEditing}
      >
        {!isLoading && renderFields()}
      </Box>

      {!isLoading && isEditing && !fields?.length && (
        <Typography sx={{ fontSize: "13px", lineHeight: "18px", color: "#455445" }}>
          No questionnaire is attached to this service yet.
        </Typography>
      )}
    </Box>
  );
};
